import { Upload } from "lucide-react"
import { useEffect, useRef, useState, type RefObject } from "react"
import { createPortal } from "react-dom"
import { toast } from "sonner"
import { useTranslation } from "@/hooks/use-translation"
import { hasOpenDialogWindow } from "@/lib/dialog-layer"
import { isFileDrag } from "@/lib/filter-files-by-accept"

type TaskFileDropOverlayProps = {
  enabled: boolean
  label: string
  onDrop: (files: File[]) => void
  /**
   * Если задан — оверлей и приём файлов только внутри этого элемента.
   * Снаружи событие не перехватывается: соседние дроп-зоны остаются своими.
   */
  boundsRef?: RefObject<HTMLElement | null>
}

type DropFrame = {
  top: number
  left: number
  width: number
  height: number
}

// Папка (и пакет macOS вроде .key) приходит в files «файлом», который не прочитать.
function droppedFiles(data: DataTransfer): { files: File[]; folders: number } {
  const items = Array.from(data.items)
  if (items.length === 0) return { files: Array.from(data.files), folders: 0 }
  const files: File[] = []
  let folders = 0
  for (const item of items) {
    if (item.kind !== "file") continue
    if (item.webkitGetAsEntry()?.isDirectory) {
      folders += 1
      continue
    }
    const file = item.getAsFile()
    if (file) files.push(file)
  }
  return { files, folders }
}

function frameAt(element: HTMLElement, event: DragEvent): DropFrame | null {
  const rect = element.getBoundingClientRect()
  const inside =
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom
  if (!inside || rect.width === 0 || rect.height === 0) return null
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
}

function sameFrame(prev: DropFrame | null, next: DropFrame): boolean {
  return (
    prev != null &&
    prev.top === next.top &&
    prev.left === next.left &&
    prev.width === next.width &&
    prev.height === next.height
  )
}

// Файлы из системы. Без boundsRef — всё окно (форма задачи). С ним — только прямоугольник панели.
export function TaskFileDropOverlay({ enabled, label, onDrop, boundsRef }: TaskFileDropOverlayProps) {
  const { t } = useTranslation("tasks")
  const [active, setActive] = useState(false)
  const [frame, setFrame] = useState<DropFrame | null>(null)
  const onDropRef = useRef(onDrop)
  const boundsRefProp = useRef(boundsRef)
  const folderMessageRef = useRef("")
  onDropRef.current = onDrop
  boundsRefProp.current = boundsRef
  folderMessageRef.current = t("files.folderSkipped")

  useEffect(() => {
    if (!enabled) return
    // Картинка, которую тянут с самой страницы, тоже несёт тип Files — такие перетаскивания не в счёт.
    let internal = false
    // Оверлей гаснет, когда курсор ушёл из окна, а не с элемента: над ним бывают меню и тосты.
    let depth = 0
    const external = (event: DragEvent) => !internal && event.dataTransfer != null && isFileDrag(event.dataTransfer)
    const startInternal = () => {
      internal = true
    }
    const endInternal = () => {
      internal = false
    }
    const clearBounds = () => {
      setFrame((prev) => (prev == null ? prev : null))
    }
    const showBounds = (next: DropFrame) => {
      setFrame((prev) => (sameFrame(prev, next) ? prev : next))
    }
    const enter = (event: DragEvent) => {
      if (!external(event)) return
      depth += 1
      if (boundsRefProp.current) return
      setActive(true)
    }
    const leave = (event: DragEvent) => {
      if (!external(event)) return
      depth = Math.max(0, depth - 1)
      if (depth !== 0) return
      setActive(false)
      const element = boundsRefProp.current?.current
      // Глубина врёт, когда курсор переходит на сам оверлей: если точка всё ещё внутри панели, не гасим.
      if (!boundsRefProp.current || !element || frameAt(element, event) == null) clearBounds()
    }
    const over = (event: DragEvent) => {
      if (!external(event) || !event.dataTransfer) return
      const bounds = boundsRefProp.current?.current
      if (boundsRefProp.current) {
        const next = bounds ? frameAt(bounds, event) : null
        if (!next || hasOpenDialogWindow()) {
          clearBounds()
          return
        }
        if (event.defaultPrevented) return
        event.preventDefault()
        event.dataTransfer.dropEffect = "copy"
        showBounds(next)
        return
      }
      if (event.defaultPrevented) return
      event.preventDefault()
      event.dataTransfer.dropEffect = "copy"
    }
    // Слушатели на окне: бросок на меню или тост поверх оверлея тоже прикрепляет файлы, а не открывает их во вкладке.
    // С boundsRef бросок мимо панели не перехватываем — его забирает та зона, над которой курсор.
    const drop = (event: DragEvent) => {
      if (!external(event) || !event.dataTransfer) return
      const bounds = boundsRefProp.current?.current
      if (boundsRefProp.current) {
        const next = bounds ? frameAt(bounds, event) : null
        clearBounds()
        depth = 0
        if (!next || hasOpenDialogWindow() || event.defaultPrevented) return
        event.preventDefault()
        const { files, folders } = droppedFiles(event.dataTransfer)
        if (folders > 0) toast.error(folderMessageRef.current, { id: "task-files-folder" })
        if (files.length > 0) onDropRef.current(files)
        return
      }
      if (event.defaultPrevented) return
      event.preventDefault()
      depth = 0
      setActive(false)
      const { files, folders } = droppedFiles(event.dataTransfer)
      if (folders > 0) toast.error(folderMessageRef.current, { id: "task-files-folder" })
      if (files.length > 0) onDropRef.current(files)
    }
    // dragend не дойдёт до окна, если источник успел исчезнуть из DOM; новое нажатие точно начинает всё заново.
    const endDrag = () => {
      endInternal()
      if (!boundsRefProp.current) return
      depth = 0
      clearBounds()
    }
    window.addEventListener("pointerdown", endInternal, true)
    window.addEventListener("dragstart", startInternal, true)
    window.addEventListener("dragend", endDrag, true)
    window.addEventListener("dragenter", enter, true)
    window.addEventListener("dragleave", leave, true)
    window.addEventListener("dragover", over)
    window.addEventListener("drop", drop)
    return () => {
      window.removeEventListener("pointerdown", endInternal, true)
      window.removeEventListener("dragstart", startInternal, true)
      window.removeEventListener("dragend", endDrag, true)
      window.removeEventListener("dragenter", enter, true)
      window.removeEventListener("dragleave", leave, true)
      window.removeEventListener("dragover", over)
      window.removeEventListener("drop", drop)
      setActive(false)
      setFrame(null)
    }
  }, [enabled])

  const bounded = boundsRef != null
  if (!enabled || (bounded ? frame == null : !active)) return null

  return createPortal(
    <div
      className={
        bounded
          ? "fixed z-[400] flex overflow-hidden bg-[var(--ui-overlay)] p-4 backdrop-blur-sm"
          : "fixed inset-0 z-[400] flex bg-[var(--ui-overlay)] p-4 backdrop-blur-sm"
      }
      style={
        bounded && frame ? { top: frame.top, left: frame.left, width: frame.width, height: frame.height } : undefined
      }
    >
      <div className="pointer-events-none flex flex-1 items-center justify-center rounded-lg border-2 border-dashed border-ui-border-strong">
        <div className="flex max-w-sm flex-col items-center gap-2 rounded-lg border border-ui-border bg-ui-bg-elevated px-6 py-5 text-center">
          <Upload className="size-5 text-ui-text-muted" aria-hidden />
          <span className="text-sm text-ui-text-primary">{label}</span>
        </div>
      </div>
    </div>,
    document.body,
  )
}
